import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { Mail, Send, Webhook, Trash2, Copy, Check, Ban } from 'lucide-react';
import {
  EMAIL_SENDERS,
  EMAIL_IDENTITY,
  TRANSACTIONAL_TEMPLATES,
  CONTENT_RULES,
  templateLabel,
  sendTransactionalEmail,
  emailWebhookUrl,
  EmailTemplate,
} from '@/lib/email';

/**
 * ADMIN → EMAIL DELIVERY → SENDING HEALTH
 * ---------------------------------------------------------------------------
 *  - The two approved From identities and the eight transactional templates.
 *  - Bounce / complaint webhook URL + the events to subscribe.
 *  - Live suppression list (auto-filled by the webhook) with manual add/release
 *    so invalid addresses are never emailed again.
 *  - The real send log (mkt_email_log) with spam-lint score per message.
 *  - A preview / test sender that uses the exact production template.
 */
const AdminEmailHealth: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';

  const [log, setLog] = useState<any[]>([]);
  const [suppressions, setSuppressions] = useState<any[]>([]);
  const [copied, setCopied] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testTpl, setTestTpl] = useState<EmailTemplate>('payment_confirmation');
  const [preview, setPreview] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [newSuppression, setNewSuppression] = useState('');

  const refresh = () => {
    supabase
      .from('mkt_email_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(25)
      .then(({ data }) => setLog(data || []));
    supabase
      .from('mkt_email_suppressions')
      .select('*')
      .order('last_seen_at', { ascending: false })
      .limit(50)
      .then(({ data }) => setSuppressions(data || []));
  };

  useEffect(refresh, []);

  const copy = (v: string) => {
    navigator.clipboard?.writeText(v);
    setCopied(v);
    setTimeout(() => setCopied(''), 1600);
  };

  const runTest = async (dryRun: boolean) => {
    setMsg('');
    setPreview(null);
    if (!dryRun && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testTo)) {
      setMsg(es ? 'Escribe una dirección válida.' : 'Enter a valid address.');
      return;
    }
    setBusy(true);
    const res: any = await sendTransactionalEmail({
      template: testTpl,
      to: dryRun ? testTo || 'preview@giggone.com' : testTo,
      language: lang,
      dryRun,
      context: {
        name: 'Raul',
        amount: '$199.00',
        plan: 'Pro',
        credits: 10,
        reference: 'GG-TEST-1042',
        jobTitle: 'Roof replacement — Fontana, CA',
        items: [
          { label: es ? 'Plan' : 'Plan', value: 'Pro' },
          { label: es ? 'Monto' : 'Amount', value: '$199.00' },
        ],
      },
    });
    setBusy(false);
    if (res?.error) setMsg(res.error);
    else if (dryRun) {
      setPreview(res);
      setMsg(es ? `Vista previa generada. Puntaje anti-spam: ${res.spamScore}/100.` : `Preview generated. Spam-lint score: ${res.spamScore}/100.`);
    } else if (res?.suppressed) {
      setMsg(es ? `Bloqueado: la dirección está suprimida (${res.reason}).` : `Blocked: address is suppressed (${res.reason}).`);
      refresh();
    } else {
      setMsg(es ? 'Enviado como correo transaccional y registrado.' : 'Sent as transactional mail and logged.');
      refresh();
    }
  };

  const addSuppression = async () => {
    const email = newSuppression.trim().toLowerCase();
    if (!email) return;
    await supabase.from('mkt_email_suppressions').upsert(
      { email, reason: 'manual', severity: 'permanent', provider_message: 'Added from the admin panel', released_at: null },
      { onConflict: 'email' }
    );
    setNewSuppression('');
    refresh();
  };

  const release = async (email: string) => {
    await supabase.from('mkt_email_suppressions').update({ released_at: new Date().toISOString() }).eq('email', email);
    refresh();
  };

  const remove = async (email: string) => {
    await supabase.from('mkt_email_suppressions').delete().eq('email', email);
    refresh();
  };

  const webhook = emailWebhookUrl();
  const events = ['delivered', 'permanent_fail', 'temporary_fail', 'complained', 'unsubscribed'];

  return (
    <div className="mt-6 space-y-6">
      {/* ── senders + templates ───────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Mail className="h-5 w-5 text-orange-500" />
          {es ? 'Identidades de envío y plantillas transaccionales' : 'Sending identities & transactional templates'}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          {es
            ? 'El correo de cuenta solo sale desde estas dos direcciones, con nombre de remitente constante. Nunca se envía desde direcciones aleatorias.'
            : 'Account mail only ever leaves from these two addresses with a constant From name. It is never sent from random addresses.'}
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {Object.values(EMAIL_SENDERS).map((s) => (
            <div key={s.key} className="rounded-xl border border-slate-200 p-4">
              <p className="font-mono text-sm font-bold text-slate-900">
                {s.name} &lt;{s.address}&gt;
              </p>
              <p className="mt-1 text-xs text-slate-500">{es ? s.use_es : s.use_en}</p>
              <p className="mt-2 text-xs text-slate-400">Reply-To: {EMAIL_IDENTITY.support}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{es ? 'Correo crítico' : 'Critical email'}</th>
                <th className="px-4 py-3">{es ? 'Remitente' : 'From'}</th>
                <th className="px-4 py-3">{es ? 'Clase' : 'Class'}</th>
                <th className="px-4 py-3">{es ? 'Espejo en el panel' : 'Dashboard mirror'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {TRANSACTIONAL_TEMPLATES.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{es ? t.label_es : t.label_en}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{EMAIL_SENDERS[t.sender].address}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
                      {es ? 'Transaccional' : 'Transactional'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-semibold text-slate-600">
                    {t.mirrored ? (es ? 'Sí — visible en el panel' : 'Yes — shown in dashboard') : es ? 'No aplica' : 'Not applicable'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 p-4">
          <p className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
            {es ? 'Reglas de contenido aplicadas en cada envío' : 'Content rules enforced on every send'}
          </p>
          <ul className="mt-2 grid gap-1.5 text-xs text-slate-600 sm:grid-cols-2">
            {CONTENT_RULES.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                {es ? r.es : r.en}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            {es
              ? 'Cada correo incluye nombre de empresa, dirección postal, correo de soporte, WhatsApp y enlace al sitio, en HTML de marca más versión de texto plano.'
              : 'Every email carries the company name, postal address, support email, WhatsApp number and website link, as branded HTML plus a plain-text part.'}
          </p>
          <p className="mt-1 font-mono text-[11px] text-slate-500">
            {EMAIL_IDENTITY.company} · {EMAIL_IDENTITY.address} · {EMAIL_IDENTITY.support} · {EMAIL_IDENTITY.whatsapp}
          </p>
        </div>
      </section>

      {/* ── webhook ───────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Webhook className="h-5 w-5 text-orange-500" />
          {es ? 'Webhook de rebotes y quejas' : 'Bounce & complaint webhook'}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          {es
            ? 'Pega esta URL en el proveedor de correo para los eventos siguientes. Cada rebote permanente, dirección inválida o queja de spam se suprime automáticamente y nunca se vuelve a contactar.'
            : 'Paste this URL into the email provider for the events below. Every permanent bounce, invalid address or spam complaint is suppressed automatically and never contacted again.'}
        </p>
        <div className="mt-3 flex items-start gap-2">
          <code className="block flex-1 break-all rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">{webhook}</code>
          <button onClick={() => copy(webhook)} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
            {copied === webhook ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {events.map((e) => (
            <span key={e} className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-xs font-bold text-slate-600">
              {e}
            </span>
          ))}
        </div>
      </section>

      {/* ── suppression list ──────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Ban className="h-5 w-5 text-orange-500" />
          {es ? 'Lista de supresión' : 'Suppression list'}
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{suppressions.length}</span>
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            value={newSuppression}
            onChange={(e) => setNewSuppression(e.target.value)}
            placeholder={es ? 'correo@ejemplo.com' : 'address@example.com'}
            className="min-w-[240px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button onClick={addSuppression} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white">
            {es ? 'Suprimir dirección' : 'Suppress address'}
          </button>
        </div>
        {suppressions.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            {es ? 'Ninguna dirección suprimida. Los rebotes duros se agregarán aquí automáticamente.' : 'No suppressed addresses. Hard bounces will be added here automatically.'}
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{es ? 'Dirección' : 'Address'}</th>
                  <th className="px-4 py-3">{es ? 'Motivo' : 'Reason'}</th>
                  <th className="px-4 py-3">{es ? 'Eventos' : 'Events'}</th>
                  <th className="px-4 py-3">{es ? 'Último' : 'Last seen'}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {suppressions.map((s) => (
                  <tr key={s.email}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                          s.released_at
                            ? 'bg-slate-50 text-slate-500 ring-slate-200'
                            : s.severity === 'permanent'
                              ? 'bg-red-50 text-red-700 ring-red-200'
                              : 'bg-amber-50 text-amber-700 ring-amber-200'
                        }`}
                      >
                        {s.released_at ? (es ? 'liberado' : 'released') : s.reason}
                      </span>
                      {s.provider_message && <p className="mt-1 max-w-[280px] truncate text-xs text-slate-400">{s.provider_message}</p>}
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-700">{s.event_count}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(s.last_seen_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {!s.released_at && (
                          <button onClick={() => release(s.email)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50">
                            {es ? 'Liberar' : 'Release'}
                          </button>
                        )}
                        <button onClick={() => remove(s.email)} className="rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── test / preview ────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Send className="h-5 w-5 text-orange-500" />
          {es ? 'Probar una plantilla real' : 'Test a real template'}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <select
            value={testTpl}
            onChange={(e) => setTestTpl(e.target.value as EmailTemplate)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
          >
            {TRANSACTIONAL_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {es ? t.label_es : t.label_en}
              </option>
            ))}
          </select>
          <input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder={es ? 'enviar a…' : 'send to…'}
            className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button onClick={() => runTest(true)} disabled={busy} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-50">
            {es ? 'Vista previa' : 'Preview'}
          </button>
          <button onClick={() => runTest(false)} disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
            {busy ? '…' : es ? 'Enviar prueba' : 'Send test'}
          </button>
        </div>
        {msg && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{msg}</p>}
        {preview && (
          <div className="mt-4 rounded-xl border border-slate-200">
            <div className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
              <p>
                <strong>From:</strong> {preview.from}
              </p>
              <p>
                <strong>Subject:</strong> {preview.subject}
              </p>
              {preview.spamFlags?.length > 0 && (
                <p className="mt-1 text-amber-700">
                  <strong>{es ? 'Alertas:' : 'Flags:'}</strong> {preview.spamFlags.join(', ')}
                </p>
              )}
            </div>
            <iframe title="email-preview" srcDoc={preview.html} className="h-[420px] w-full rounded-b-xl bg-slate-50" />
            <details className="border-t border-slate-100 p-4">
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-slate-500">
                {es ? 'Versión de texto plano' : 'Plain-text version'}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-600">{preview.text}</pre>
            </details>
          </div>
        )}
      </section>

      {/* ── send log ──────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-extrabold text-slate-900">{es ? 'Registro de envíos' : 'Send log'}</h2>
        {log.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">{es ? 'Aún no hay envíos registrados.' : 'No sends logged yet.'}</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{es ? 'Plantilla' : 'Template'}</th>
                  <th className="px-4 py-3">{es ? 'Destinatario' : 'Recipient'}</th>
                  <th className="px-4 py-3">{es ? 'Estado' : 'Status'}</th>
                  <th className="px-4 py-3">{es ? 'Anti-spam' : 'Spam lint'}</th>
                  <th className="px-4 py-3">{es ? 'Fecha' : 'Date'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {log.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{templateLabel(r.template, lang)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.to_email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                          ['sent', 'delivered'].includes(r.status)
                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                            : r.status === 'suppressed'
                              ? 'bg-slate-50 text-slate-600 ring-slate-200'
                              : 'bg-red-50 text-red-700 ring-red-200'
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-700">{r.spam_score ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminEmailHealth;
